import { io, type Socket } from 'socket.io-client'

/**
 * The parent tier's push channel — see `ParentEventsGateway` on the server.
 *
 * 🔴 WHY A SOCKET AND NOT A REFETCH. An announcement's whole value is that a
 * family finds out BEFORE they act on the old answer: a parent who learns at 8am
 * that today is a half day has already made the packed lunch and left. The
 * WhatsApp message is the durable path — queued behind an opt-in, quiet hours
 * and a retry schedule — and this is the fast one, for the parent who happens to
 * have the app open.
 *
 * 🔴 THE FRAME CARRIES SCHOOL-WIDE COPY AND NOTHING ELSE, WHICH IS WHY IT MAY BE
 * RENDERED DIRECTLY. `staffEvents.ts` states the opposite rule for its channel —
 * *"a pointer, not a payload"* — and the difference is real rather than a
 * relaxation: an announcement is a fact about the SCHOOL, identical for every
 * family, and the server's own model holds no `playerId` for it. Anything about
 * a child would still have to be fetched, scoped and authorised like every other
 * read.
 *
 * ⚠️ IT IS NOT A DELIVERY GUARANTEE AND NOTHING MAY DEPEND ON IT. A phone in a
 * pocket misses the frame; the announcement is in the list the screen loads
 * either way, and the message is in an outbox that retries.
 */

/** 🔴 School-wide copy only — see above. Mirrors `ParentEvent` on the server. */
export interface ParentPushEvent {
  /** e.g. `announcement.posted`. Namespaced so a screen can filter cheaply. */
  type: string
  /** The announcement's own id, so a screen can dedupe against what it fetched. */
  id: string
  title: string
  detail?: string
  /** Who it is from — the school, or the office. */
  from?: string
  /** ISO 8601. When the thing happened, not when the frame arrived. */
  at: string
}

/**
 * ⚠️ THE SAME BASE THE REST CLIENT USES, AND READ THE SAME WAY. Blank in dev, so
 * the socket goes to the current origin and through Vite's proxy; in production
 * the app and the API are the same origin. `client.ts` states the rule this
 * follows — *"sending a credential to whatever host an env var happened to name
 * is how a credential leaves the building"* — and this connection carries an
 * access token in its handshake.
 */
function baseUrl(): string {
  return import.meta.env.VITE_API_BASE_URL ?? ''
}

/**
 * Open the parent channel, and return the function that closes it.
 *
 * 🔴 THE TOKEN GOES IN `auth`, NEVER IN THE QUERY STRING. A query parameter
 * lands in access logs and in proxy logs, which is where credentials go to be
 * found later. The server reads `handshake.auth.token`, checks the role is
 * PARENT, and disconnects anything else.
 *
 * 🔴 THE CALLER GETS A DISPOSER RATHER THAN THE SOCKET. A component holding the
 * socket is a component that can emit on it, and this channel is one-way by
 * design — the server mounts no inbound handlers at all.
 *
 * ⚠️ RECONNECTION IS socket.io's OWN AND IT IS DELIBERATELY BOUNDED HERE. A
 * parent's access token is fifteen minutes with no refresh route (OD-51), so
 * once it expires every reconnect is refused — and socket.io's default is to try
 * forever, which on a phone left open overnight is a battery drain against a
 * door that will not open until the parent signs in again.
 */
export function openParentEvents(
  token: string,
  onEvent: (event: ParentPushEvent) => void,
): () => void {
  const socket: Socket = io(`${baseUrl()}/parent`, {
    auth: { token },
    /*
     * ⚠️ WEBSOCKET FIRST, WITH POLLING AS THE FALLBACK — socket.io's default,
     * stated rather than assumed. A parent behind a mobile proxy that strips
     * upgrades still gets the channel; without the fallback they get silence.
     */
    transports: ['websocket', 'polling'],
    withCredentials: true,
    /* See the note above — an expired token cannot be retried into working. */
    reconnectionAttempts: 10,
    reconnectionDelayMax: 20_000,
  })

  socket.on('parent.event', (event: ParentPushEvent) => {
    onEvent(event)
  })

  return () => {
    /* ⚠️ `off` BEFORE `disconnect`: a frame delivered during teardown would
       otherwise call into a component that has already unmounted. */
    socket.off('parent.event')
    socket.disconnect()
  }
}
